import { useRef } from 'react';

import { useTheme } from '../../hooks/useTheme';

import AccountHeader from './AccountHeader';
import AccountMenuItems from './AccountMenuItems';
import ModeSwitchItem from './ModeSwitchItem';
import { NAV_THEMES } from './nav-tokens';
import NavDrawerHeader from './NavDrawerHeader';
import NavEntryLink from './NavEntryLink';
import NavSectionDisclosure from './NavSectionDisclosure';
import SignOutItem from './SignOutItem';
import type { AppModeInfo } from './use-app-mode';
import type { NavModel } from './use-nav-model';
import { useDrawerFocusTrap } from './useDrawerFocusTrap';
import { useExpandedSection } from './useExpandedSection';

interface NebariNavDrawerProps {
  nav: NavModel;
  viewer: AppModeInfo;
  onClose: () => void;
}

/** The mobile nav in full: every entry, sections expandable to their pages, then
 *  the account actions and Sign out. Opened by the hamburger and by More. */
export default function NebariNavDrawer({ nav, viewer, onClose }: NebariNavDrawerProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const panelRef = useRef<HTMLDivElement>(null);
  const { expanded, toggle } = useExpandedSection(nav.activeSection);

  useDrawerFocusTrap(panelRef, onClose);

  return (
    <div
      className="nb-drawer-scrim-enter fixed inset-0 z-50 flex navtab:hidden"
      style={{ background: 'rgba(15, 25, 18, 0.35)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="flex h-full w-[300px] max-w-[85%] flex-col overflow-hidden shadow-2xl"
        style={{ background: t.bg, color: t.ink }}
        onClick={(e) => e.stopPropagation()}
      >
        <NavDrawerHeader onClose={onClose} />
        <nav aria-label="Sections" className="flex-1 overflow-y-auto px-2 py-3">
          {/* A mode with no destinations would put the heading over an empty
              list. The account rows below are why the drawer is still here. */}
          {nav.entries.length > 0 && (
            <div
              className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ color: t.muted }}
            >
              Go to
            </div>
          )}
          {nav.entries.map((entry) =>
            entry.kind === 'link' ? (
              <NavEntryLink
                key={entry.link.id}
                link={entry.link}
                collapsed={false}
                onNavigate={onClose}
              />
            ) : (
              <NavSectionDisclosure
                key={entry.section.id}
                section={entry.section}
                items={entry.items}
                expanded={expanded === entry.section.id}
                onToggle={() => toggle(entry.section.id)}
                itemVariant="drawer"
                onNavigate={onClose}
              />
            ),
          )}
          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${t.border}` }}>
            <div
              className="px-4 pb-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ color: t.muted }}
            >
              Account
            </div>
            <AccountHeader viewer={viewer} />
            <AccountMenuItems onNavigate={onClose} />
            {/* Drawer only. Above `navtab` the top bar carries the switch, and
                rendering it in the shared account rows would put it on screen
                twice there. */}
            <ModeSwitchItem viewer={viewer} onNavigate={onClose} />
          </div>
        </nav>
        <div className="px-2 py-2" style={{ borderTop: `1px solid ${t.border}` }}>
          <SignOutItem variant="drawer" />
        </div>
      </div>
    </div>
  );
}
